import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

import {
  cellHeatmapStylePlain,
  computeColumnRanges,
  type ColumnRange,
  type HeatmapScale,
} from '../utils/conditionalFormat'
import { scaleForColumn } from '../utils/tableHeatmapScales'

interface ConditionalFormatContextValue {
  enabled: boolean
  setEnabled: (value: boolean) => void
  ranges: Record<string, ColumnRange>
  cellStyle: (columnKey: string, value: unknown) => React.CSSProperties | undefined
}

const ConditionalFormatContext = createContext<ConditionalFormatContextValue | null>(null)

export function ConditionalFormatProvider<T>({
  rows,
  columnKeys,
  columnScales = {},
  getValue,
  defaultEnabled = true,
  children,
}: {
  rows: readonly T[]
  columnKeys: readonly string[]
  /** Per-column palette; defaults to `strength` when omitted. */
  columnScales?: Record<string, HeatmapScale>
  getValue: (row: T, columnKey: string) => unknown
  defaultEnabled?: boolean
  children: ReactNode
}) {
  const [enabled, setEnabled] = useState(defaultEnabled)
  const ranges = useMemo(
    () => computeColumnRanges(rows, columnKeys, getValue),
    [rows, columnKeys, getValue],
  )

  const value = useMemo<ConditionalFormatContextValue>(
    () => ({
      enabled,
      setEnabled,
      ranges,
      cellStyle: (columnKey, cellValue) =>
        cellHeatmapStylePlain(
          cellValue,
          ranges[columnKey],
          enabled,
          scaleForColumn(columnKey, columnScales),
        ),
    }),
    [columnScales, enabled, ranges],
  )

  return (
    <ConditionalFormatContext.Provider value={value}>{children}</ConditionalFormatContext.Provider>
  )
}

export function useConditionalFormat() {
  const ctx = useContext(ConditionalFormatContext)
  if (!ctx) {
    throw new Error('useConditionalFormat must be used within ConditionalFormatProvider')
  }
  return ctx
}

export function useOptionalConditionalFormat() {
  return useContext(ConditionalFormatContext)
}
